/**
 * SchoolDetailPanel.tsx
 * Painel interativo de detalhes da escola — KPI cards clicáveis, avatares em wrap,
 * card de Turnos e dialog de grade de horários por turma.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { simulationApi } from '../../services/simulationApi';
import type { DemoSchool } from '../../types';

// ── Tipos ────────────────────────────────────────────────────────────────────
interface Actor          { id: string; nome: string; fotoUrl?: string | null; genero?: string | null; cargo?: string | null; matricula?: string | null; email?: string | null; }
interface AlunoActor     { id: string; nome: string; fotoUrl?: string | null; genero?: string | null; matricula?: string | null; hasTag: boolean; totalTags: number; }
interface ProfessorActor { id: string; nome: string; fotoUrl?: string | null; genero?: string | null; email?: string | null; materiaPrincipal?: string | null; turmas: { id: string; nome: string; serie: string; turno: string }[]; }
interface Horario        { id: string; diaSemana: string; numeroAula: number; horaInicio?: string | null; horaFim?: string | null; tipo: string; descricao?: string | null; materia?: { nome: string; codigo: string } | null; professor?: { nome: string; fotoUrl?: string | null } | null; }
interface Turma          { id: string; nome: string; serie: string; turno: string; anoLetivo: number; capacidade?: number | null; totalAlunos: number; professores: Actor[]; horarios: Horario[]; }
interface Turno          { nome: string; totalTurmas: number; totalAlunos: number; }
interface Dispositivo    { id: string; nome: string; codigo: string; tipo: string; localInstalacao?: string | null; status: string; ultimoHeartbeatEm?: string | null; identificadorFisico?: string | null; ipLocal?: string | null; }
interface Municipio      { id: string; nome: string; nomeOficial?: string | null; nomePrefeito?: string | null; cargoPrefeito?: string | null; nomeVicePrefeito?: string | null; nomeSecretario?: string | null; fotoPrefeito?: string | null; logoUrl?: string | null; corPrimaria?: string | null; siteOficial?: string | null; telefone?: string | null; emailInstitucional?: string | null; slogan?: string | null; }
interface ResponsavelWithPush extends Actor { dispositivosPush?: { plataforma: string; ultimoUsoEm?: string | null }[]; }
interface PanelData      { turmas: Turma[]; turnos: Turno[]; alunos: AlunoActor[]; alunosMaisDisponiveis?: boolean; professores: ProfessorActor[]; gestores: Actor[]; responsaveis: ResponsavelWithPush[]; dispositivos: Dispositivo[]; municipio: Municipio | null; totais: Record<string, number>; }

// ── Constantes ───────────────────────────────────────────────────────────────
const DIAS_SEMANA = ['SEGUNDA','TERCA','QUARTA','QUINTA','SEXTA','SABADO'];
const DIA_LABELS: Record<string, string> = { SEGUNDA:'Seg', TERCA:'Ter', QUARTA:'Qua', QUINTA:'Qui', SEXTA:'Sex', SABADO:'Sáb' };
const TURNO_LABELS: Record<string, string> = { MANHA:'Manhã', TARDE:'Tarde', NOITE:'Noite', INTEGRAL:'Integral', SEM_TURNO:'S/Turno' };
const TURNO_COLORS: Record<string, string> = { MANHA:'#f9e2af', TARDE:'#fab387', NOITE:'#89b4fa', INTEGRAL:'#a6e3a1', SEM_TURNO:'#6c7086' };

const CARD_CONFIG = [
  { key: 'alunos',       icon: '🎓', label: 'Alunos',       color: '#74c7ec' },
  { key: 'turmas',       icon: '📚', label: 'Turmas',       color: '#89dceb' },
  { key: 'turnos',       icon: '🕐', label: 'Turnos',       color: '#f9e2af' },
  { key: 'professores',  icon: '👨‍🏫', label: 'Professores',  color: '#89b4fa' },
  { key: 'responsaveis', icon: '👨‍👩‍👧', label: 'Responsáveis', color: '#cba6f7' },
  { key: 'gestores',     icon: '🏛️',  label: 'Gestores',     color: '#a6e3a1' },
  { key: 'dispositivo',  icon: '📡', label: 'Dispositivo',  color: '#f5c2e7' },
  { key: 'chaveiros',    icon: '🔑', label: 'Chaveiros',    color: '#fab387' },
  { key: 'municipio',    icon: '🏙️', label: 'Município',    color: '#94e2d5' },
  { key: 'appInstalado', icon: '📱', label: 'App',          color: '#a6e3a1' },
];

// ── Avatar mini ───────────────────────────────────────────────────────────────
function MiniAvatar({ actor, size = 40, theme = 'dark' }: { actor: Actor; size?: number; theme?: 'dark' | 'light' }) {
  const isFem = actor.genero === 'FEMININO';
  const fallback = isFem ? '/img/avatars/f/01.jpg' : '/img/avatars/m/01.jpg';
  const isLight = theme === 'light';
  return (
    <div
      title={actor.nome}
      style={{ width: size, height: size, flexShrink: 0 }}
      className={`rounded-full overflow-hidden border-2 transition-all cursor-default ${
        isLight ? 'border-slate-200 hover:border-slate-400' : 'border-white/10 hover:border-white/40'
      }`}
    >
      <img
        src={actor.fotoUrl || fallback}
        alt={actor.nome}
        className="w-full h-full object-cover"
        onError={e => { (e.target as HTMLImageElement).src = fallback; }}
      />
    </div>
  );
}

// ── Avatar com nome (grade de turma) ─────────────────────────────────────────
function AvatarCard({ actor, sub, theme = 'dark' }: { actor: Actor; sub?: string; theme?: 'dark' | 'light' }) {
  const isFem = actor.genero === 'FEMININO';
  const fallback = isFem ? '/img/avatars/f/01.jpg' : '/img/avatars/m/01.jpg';
  const isLight = theme === 'light';
  return (
    <div className="flex flex-col items-center gap-1 w-[64px]">
      <div className={`w-10 h-10 rounded-full overflow-hidden border ${isLight ? 'border-slate-200' : 'border-white/15'}`}>
        <img src={actor.fotoUrl || fallback} alt={actor.nome} className="w-full h-full object-cover"
             onError={e => { (e.target as HTMLImageElement).src = fallback; }} />
      </div>
      <span className={`text-[10px] text-center leading-tight line-clamp-2 w-full font-medium ${isLight ? 'text-slate-700' : 'text-[#cdd6f4]'}`}>{actor.nome.split(' ')[0]}</span>
      {sub && <span className={`text-[8px] text-center ${isLight ? 'text-slate-400' : 'text-[#6c7086]'}`}>{sub}</span>}
    </div>
  );
}

// ── Dialog de grade da turma ──────────────────────────────────────────────────
function TurmaDialog({ turma, onClose, theme = 'dark' }: { turma: Turma; onClose: () => void; theme?: 'dark' | 'light' }) {
  const isLight = theme === 'light';
  // Agrupa horários por dia
  const byDay: Record<string, Horario[]> = {};
  for (const h of turma.horarios) {
    if (!byDay[h.diaSemana]) byDay[h.diaSemana] = [];
    byDay[h.diaSemana].push(h);
  }
  const diasPresentes = DIAS_SEMANA.filter(d => byDay[d]?.length);
  const maxSlots = Math.max(...DIAS_SEMANA.map(d => byDay[d]?.length ?? 0), 1);
  const slots = Array.from({ length: maxSlots }, (_, i) => i + 1);

  const hasHorarios = turma.horarios.length > 0;

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <div
        className={`relative z-10 w-full max-w-2xl rounded-2xl border shadow-2xl flex flex-col max-h-[85vh] overflow-hidden`}
        style={{ 
          background: isLight ? 'linear-gradient(160deg,#ffffff 0%,#f8fafc 100%)' : 'linear-gradient(160deg,#1e1e2e 0%,#181825 100%)',
          borderColor: isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.1)'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`px-5 py-4 border-b flex items-start justify-between flex-shrink-0 ${isLight ? 'border-slate-100' : 'border-white/8'}`}>
          <div>
            <h2 className={`text-sm font-bold ${isLight ? 'text-slate-800' : 'text-white'}`}>{turma.nome}</h2>
            <div className="flex gap-3 mt-1 flex-wrap">
              <span className={`text-[10px] ${isLight ? 'text-slate-400' : 'text-[#6c7086]'}`}>Série: <span className={isLight ? 'text-slate-700 font-semibold' : 'text-[#a6adc8]'}>{turma.serie}</span></span>
              <span className={`text-[10px] ${isLight ? 'text-slate-400' : 'text-[#6c7086]'}`}>Turno: <span style={{ color: TURNO_COLORS[turma.turno], fontWeight: 600 }}>{TURNO_LABELS[turma.turno] ?? turma.turno}</span></span>
              <span className={`text-[10px] ${isLight ? 'text-slate-400' : 'text-[#6c7086]'}`}>Alunos: <span className="text-[#2563eb] font-semibold">{turma.totalAlunos}</span></span>
              <span className={`text-[10px] ${isLight ? 'text-slate-400' : 'text-[#6c7086]'}`}>Ano: <span className={isLight ? 'text-slate-700 font-semibold' : 'text-[#a6adc8]'}>{turma.anoLetivo}</span></span>
              <span className={`text-[10px] ${isLight ? 'text-slate-400' : 'text-[#6c7086]'}`}>Aulas/sem: <span className="text-emerald-600 font-semibold">{turma.horarios.filter(h => h.tipo === 'AULA').length}</span></span>
            </div>
          </div>
          <button onClick={onClose} className={`w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer text-sm bg-transparent border-none ${isLight ? 'text-slate-400 hover:text-slate-700 hover:bg-slate-100' : 'text-[#6c7086] hover:text-white hover:bg-white/5'}`}>✕</button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-5">

          {/* Grade de horários */}
          <div>
            <div className={`text-[10px] font-bold uppercase tracking-widest mb-3 ${isLight ? 'text-violet-600' : 'text-[#cba6f7]/70'}`}>Grade de Horários</div>
            {!hasHorarios ? (
              <div className={`text-xs text-center py-6 rounded-xl border ${isLight ? 'border-slate-200 bg-slate-50 text-slate-400' : 'border-white/5 bg-white/2 text-[#6c7086]'}`}>
                Nenhum horário cadastrado nesta turma.<br />
                <span className="text-[10px] opacity-60">A grade é configurada no painel de gestão escolar.</span>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[10px]" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                  <thead>
                    <tr>
                      <th className={`text-left font-bold pb-2 pr-3 w-[40px] ${isLight ? 'text-slate-400' : 'text-[#6c7086]'}`}>Aula</th>
                      {diasPresentes.map(d => (
                        <th key={d} className={`text-center font-bold pb-2 px-1 min-w-[80px] ${isLight ? 'text-slate-700' : 'text-[#a6adc8]'}`}>{DIA_LABELS[d]}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {slots.map(slot => (
                      <tr key={slot}>
                        <td className={`pr-3 py-1 text-center font-bold ${isLight ? 'text-slate-400' : 'text-[#6c7086]'}`}>{slot}ª</td>
                        {diasPresentes.map(dia => {
                          const h = byDay[dia]?.find(x => x.numeroAula === slot);
                          if (!h) return <td key={dia} className="px-1 py-1" />;
                          const isAula = h.tipo === 'AULA';
                          return (
                            <td key={dia} className="px-1 py-1">
                              <div
                                className="rounded-lg px-1.5 py-1.5 flex flex-col gap-0.5"
                                style={{
                                  background: isAula ? (isLight ? 'rgba(37,99,235,0.06)' : 'rgba(137,180,250,0.08)') : (isLight ? 'rgba(217,119,6,0.04)' : 'rgba(249,226,175,0.06)'),
                                  border: `1px solid ${isAula ? (isLight ? 'rgba(37,99,235,0.15)' : 'rgba(137,180,250,0.2)') : (isLight ? 'rgba(217,119,6,0.1)' : 'rgba(249,226,175,0.15)')}`,
                                }}
                              >
                                <span className={`font-bold truncate text-[9px] ${isLight ? 'text-slate-800' : 'text-[#cdd6f4]'}`}>
                                  {h.materia?.nome ?? h.descricao ?? (isAula ? 'Aula' : 'Intervalo')}
                                </span>
                                {h.horaInicio && (
                                  <span className={`text-[8px] ${isLight ? 'text-slate-500' : 'text-[#6c7086]'}`}>{h.horaInicio}–{h.horaFim}</span>
                                )}
                                {h.professor && (
                                  <span className={`truncate text-[8px] font-semibold ${isLight ? 'text-blue-600' : 'text-[#89b4fa]'}`}>{h.professor.nome.split(' ')[0]}</span>
                                )}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Professores vinculados à turma */}
          {turma.professores.length > 0 && (
            <div>
              <div className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${isLight ? 'text-indigo-600' : 'text-[#89b4fa]/70'}`}>Professores da Turma</div>
              <div className="flex flex-wrap gap-3">
                {turma.professores.map(p => <AvatarCard key={p.id} actor={p} theme={theme} />)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Dialog de detalhe do turno ───────────────────────────────────────────────
function TurnoDialog({ turnoNome, data, onClose, theme = 'dark' }: { turnoNome: string; data: PanelData; onClose: () => void; theme?: 'dark' | 'light'; }) {
  const isLight = theme === 'light';
  const color = TURNO_COLORS[turnoNome] ?? '#6c7086';
  const label = TURNO_LABELS[turnoNome] ?? turnoNome;
  const turnoInfo = data.turnos.find(t => t.nome === turnoNome);
  const turmasDoTurno = data.turmas.filter(t => t.turno === turnoNome);

  // Calcula total de aulas por semana neste turno
  const totalAulas = turmasDoTurno.reduce((acc, t) => acc + t.horarios.filter(h => h.tipo === 'AULA').length, 0);

  // Professores únicos atuando neste turno
  const profSet = new Map<string, Actor>();
  for (const turma of turmasDoTurno) {
    for (const p of turma.professores) profSet.set(p.id, p);
  }
  const profsTurno = Array.from(profSet.values());

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <div
        className={`relative z-10 w-full max-w-lg rounded-2xl border shadow-2xl flex flex-col max-h-[85vh] overflow-hidden`}
        style={{ 
          background: isLight ? 'linear-gradient(160deg,#ffffff 0%,#f8fafc 100%)' : 'linear-gradient(160deg,#1e1e2e 0%,#181825 100%)', 
          borderColor: isLight ? 'rgba(0,0,0,0.08)' : `${color}30` 
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`px-5 py-4 border-b flex items-center justify-between flex-shrink-0 ${isLight ? 'border-slate-100' : 'border-white/8'}`}
             style={{ background: isLight ? '#f8fafc' : `${color}08` }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center text-lg"
                 style={{ background: `${color}20` }}>🕐</div>
            <div>
              <h2 className="text-sm font-bold" style={{ color: isLight && color === '#f9e2af' ? '#d97706' : color }}>{label}</h2>
              <p className={`text-[10px] mt-0.5 ${isLight ? 'text-slate-400' : 'text-[#6c7086]'}`}>Detalhes do turno</p>
            </div>
          </div>
          <button onClick={onClose} className={`w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer text-sm bg-transparent border-none ${isLight ? 'text-slate-400 hover:text-slate-700 hover:bg-slate-100' : 'text-[#6c7086] hover:text-white hover:bg-white/5'}`}>✕</button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-5">

          {/* KPIs do turno */}
          <div className="grid grid-cols-3 gap-2">
            {[
              { label: 'Turmas', value: turnoInfo?.totalTurmas ?? 0, icon: '📚' },
              { label: 'Alunos', value: turnoInfo?.totalAlunos ?? 0, icon: '🎓' },
              { label: 'Aulas/sem', value: totalAulas, icon: '📋' },
            ].map(k => (
              <div key={k.label} className={`flex flex-col items-center gap-1 rounded-xl py-3 border ${isLight ? 'border-slate-200 shadow-sm' : 'border-white/8'}`}
                   style={{ background: isLight ? '#f8fafc' : `${color}08` }}>
                <span className="text-base">{k.icon}</span>
                <span className="text-lg font-bold" style={{ color: isLight && color === '#f9e2af' ? '#d97706' : color }}>{k.value}</span>
                <span className={`text-[9px] uppercase tracking-wider ${isLight ? 'text-slate-400' : 'text-[#6c7086]'}`}>{k.label}</span>
              </div>
            ))}
          </div>

          {/* Turmas do turno */}
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: isLight && color === '#f9e2af' ? '#d97706' : `${color}99` }}>Turmas neste turno</div>
            <div className="flex flex-col gap-1.5">
              {turmasDoTurno.map(t => (
                <div key={t.id}
                     className={`flex items-center gap-3 rounded-xl px-3 py-2 border ${isLight ? 'bg-white border-slate-200 shadow-sm' : 'border-white/6 bg-white/2'}`}>
                  <div className="flex-1 min-w-0">
                    <div className={`text-[11px] font-bold truncate ${isLight ? 'text-slate-800' : 'text-white'}`}>{t.nome}</div>
                    <div className={`text-[9px] mt-0.5 ${isLight ? 'text-slate-500' : 'text-[#6c7086]'}`}>
                      {t.serie} · {t.totalAlunos} alunos · {t.horarios.filter(h => h.tipo === 'AULA').length} aulas/sem
                    </div>
                  </div>
                  {/* Mini grade de dias */}
                  <div className="flex gap-0.5">
                    {['SEGUNDA','TERCA','QUARTA','QUINTA','SEXTA'].map(d => {
                      const hasAula = t.horarios.some(h => h.diaSemana === d && h.tipo === 'AULA');
                      return (
                        <div key={d}
                             title={DIA_LABELS[d]}
                             className="w-4 h-4 rounded-sm text-[5px] flex items-center justify-center font-bold"
                             style={{
                               background: hasAula ? `${color}30` : (isLight ? '#f1f5f9' : 'rgba(255,255,255,0.04)'),
                               color: hasAula ? (isLight ? '#0f172a' : color) : (isLight ? '#94a3b8' : '#45475a'),
                               border: `1px solid ${hasAula ? color + '40' : (isLight ? '#e2e8f0' : 'rgba(255,255,255,0.06)')}`,
                             }}>
                          {DIA_LABELS[d][0]}
                        </div>
                      );
                    })}
                  </div>
                  {/* Professores mini */}
                  <div className="flex -space-x-1.5">
                    {t.professores.slice(0, 3).map(p => <MiniAvatar key={p.id} actor={p} size={22} theme={theme} />)}
                  </div>
                </div>
              ))}
              {turmasDoTurno.length === 0 && <Empty msg="Nenhuma turma neste turno" />}
            </div>
          </div>

          {/* Professores do turno */}
          {profsTurno.length > 0 && (
            <div>
              <div className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${isLight ? 'text-slate-400' : 'text-[#89b4fa66]'}`}>Professores atuando neste turno</div>
              <div className="flex flex-wrap gap-2">
                {profsTurno.map(p => <MiniAvatar key={p.id} actor={p} size={36} theme={theme} />)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── KPI Card clicável ─────────────────────────────────────────────────────────
function KpiCard({ icon, label, value, color, isActive, onClick, theme = 'dark' }: {
  icon: string; label: string; value: number; color: string; isActive: boolean; onClick: () => void; theme?: 'dark' | 'light';
}) {
  const isLight = theme === 'light';
  const displayColor = isLight 
    ? (color === '#a6e3a1' ? '#059669' : color === '#74c7ec' ? '#2563eb' : color === '#f5c2e7' ? '#db2777' : color === '#cba6f7' ? '#7c3aed' : color === '#f9e2af' ? '#d97706' : color) 
    : color;
  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center gap-1 px-3 py-2.5 rounded-xl border transition-all cursor-pointer ${
        isActive 
          ? isLight 
            ? 'shadow-sm' 
            : '' 
          : 'hover:bg-black/5'
      }`}
      style={{
        background:  isActive 
          ? `${color}15` 
          : (isLight ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.03)'),
        borderColor: isActive 
          ? `${color}60` 
          : (isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)'),
        boxShadow:   isActive ? `0 0 12px ${color}20` : 'none',
        minWidth: 68,
      }}
    >
      <span className="text-lg leading-none">{icon}</span>
      <span className="text-base font-extrabold leading-none" style={{ color: displayColor }}>{value}</span>
      <span className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: isActive ? displayColor : (isLight ? '#64748b' : '#6c7086') }}>{label}</span>
    </button>
  );
}

// ── Seção de conteúdo por card ────────────────────────────────────────────────
function CardSection({ activeCard, data, theme = 'dark' }: { activeCard: string; data: PanelData; theme?: 'dark' | 'light' }) {
  const [turmaDialogId, setTurmaDialogId] = useState<string | null>(null);
  const [turnoDialogNome, setTurnoDialogNome] = useState<string | null>(null);
  const turmaDialog = turmaDialogId ? data.turmas.find(t => t.id === turmaDialogId) : null;
  const isLight = theme === 'light';

  if (activeCard === 'alunos') {
    const alunosCast  = data.alunos as AlunoActor[];
    const comTag      = alunosCast.filter(a => a.hasTag).length;
    const semTag      = alunosCast.length - comTag;
    const totalReal   = data.totais.alunos;
    const exibidos    = alunosCast.length;
    const restantes   = totalReal - exibidos;
    return (
      <SectionWrapper title={`${totalReal} Alunos matriculados`} color="#74c7ec" theme={theme}>
        <StatRow items={[
          { label: 'Turmas',          value: data.totais.turmas },
          { label: 'Com Responsável', value: data.totais.responsaveis },
          { label: 'Com Chaveiro 🔑', value: comTag },
          { label: 'Sem Chaveiro ⚠️',  value: semTag },
        ]} theme={theme} />
        {exibidos < totalReal && (
          <div className={`text-[10px] font-semibold rounded-lg px-2.5 py-1.5 text-center ${isLight ? 'text-amber-700 bg-amber-50 border border-amber-200' : 'text-[#fab387] bg-[#fab387]/10'}`}>
            Exibindo {exibidos} de {totalReal} — +{restantes} não carregados
          </div>
        )}
        {/* Grid de avatares com indicador de RFID */}
        <div className="flex flex-wrap gap-2">
          {alunosCast.map(a => {
            const isFem  = a.genero === 'FEMININO';
            const fallback = isFem ? '/img/avatars/f/01.jpg' : '/img/avatars/m/01.jpg';
            return (
              <div key={a.id} className="relative">
                <div className={`w-9 h-9 rounded-full overflow-hidden border ${isLight ? 'border-slate-200' : 'border-white/10'}`}>
                  <img src={a.fotoUrl || fallback} alt={a.nome}
                       className="w-full h-full object-cover"
                       onError={e => { (e.target as HTMLImageElement).src = fallback; }} />
                </div>
                {/* Indicador RFID */}
                {a.hasTag ? (
                  <div className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-[#a6e3a1] border flex items-center justify-center ${isLight ? 'border-white' : 'border-[#1e1e2e]'}`}>
                    <svg width="7" height="7" viewBox="0 0 7 7" fill="none">
                      <path d="M1 3.5L2.8 5.5L6 1.5" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                ) : (
                  <div className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-[#f38ba8]/80 border flex items-center justify-center ${isLight ? 'border-white' : 'border-[#1e1e2e]'}`}>
                    <span className="text-white font-bold" style={{ fontSize: 7, lineHeight: 1 }}>!</span>
                  </div>
                )}
              </div>
            );
          })}
          {alunosCast.length === 0 && <Empty msg="Nenhum aluno matriculado" theme={theme} />}
        </div>
        {data.alunosMaisDisponiveis && (
          <button className={`w-full mt-2 py-2 rounded-xl border border-dashed transition-all cursor-pointer text-xs font-bold ${isLight ? 'border-blue-300 text-blue-600 hover:bg-blue-50' : 'border-[#74c7ec]/30 text-[#74c7ec] hover:bg-[#74c7ec]/5'}`}>
            ↓ Carregar mais {restantes} alunos (página 2)
          </button>
        )}
      </SectionWrapper>
    );
  }

  if (activeCard === 'turmas') return (
    <SectionWrapper title={`${data.totais.turmas} Turmas ativas`} color="#89dceb" theme={theme}>
      <div className="flex flex-col gap-2">
        {data.turmas.map(t => (
          <button
            key={t.id}
            onClick={() => setTurmaDialogId(t.id)}
            className={`flex items-center gap-3 rounded-xl px-3 py-2.5 border transition-all text-left cursor-pointer w-full ${isLight ? 'bg-white border-slate-200 hover:border-cyan-400 hover:bg-cyan-50/20 shadow-sm' : 'border-white/8 bg-white/3 hover:border-[#89dceb]/30 hover:bg-[#89dceb]/5'}`}
          >
            <span className="text-base">📚</span>
            <div className="flex-1 min-w-0">
              <div className={`text-xs font-bold truncate ${isLight ? 'text-slate-800' : 'text-white'}`}>{t.nome}</div>
              <div className={`text-[9px] mt-0.5 ${isLight ? 'text-slate-500' : 'text-[#6c7086]'}`}>
                {t.serie} · <span style={{ color: TURNO_COLORS[t.turno], fontWeight: 600 }}>{TURNO_LABELS[t.turno] ?? t.turno}</span> · {t.totalAlunos} alunos · {t.horarios.filter(h => h.tipo === 'AULA').length} aulas/sem
              </div>
            </div>
            <div className="flex -space-x-1.5">
              {t.professores.slice(0, 3).map(p => <MiniAvatar key={p.id} actor={p} size={24} theme={theme} />)}
            </div>
            <span className={`text-xs ${isLight ? 'text-cyan-600' : 'text-[#89dceb]/40'}`}>→</span>
          </button>
        ))}
        {data.turmas.length === 0 && <Empty msg="Nenhuma turma cadastrada" theme={theme} />}
      </div>
      {turmaDialog && <TurmaDialog turma={turmaDialog} onClose={() => setTurmaDialogId(null)} theme={theme} />}
    </SectionWrapper>
  );

  if (activeCard === 'turnos') return (
    <SectionWrapper title={`${data.totais.turnos} Turnos ativos`} color="#f9e2af" theme={theme}>
      <div className="flex flex-col gap-2">
        {data.turnos.map(t => (
          <button
            key={t.nome}
            onClick={() => setTurnoDialogNome(t.nome)}
            className={`flex items-center gap-3 rounded-xl px-3 py-2.5 border transition-all text-left cursor-pointer w-full ${isLight ? 'bg-white border-slate-200 hover:bg-amber-50/10 hover:border-amber-400 shadow-sm' : 'border-white/8 bg-white/3 hover:bg-[#f9e2af]/5 hover:border-[#f9e2af]/25'}`}
          >
            <span className="text-base">🕐</span>
            <div className="flex-1">
              <div className="text-xs font-bold" style={{ color: TURNO_COLORS[t.nome] }}>{TURNO_LABELS[t.nome] ?? t.nome}</div>
              <div className={`text-[10px] mt-0.5 ${isLight ? 'text-slate-500' : 'text-[#6c7086]'}`}>{t.totalTurmas} turmas · {t.totalAlunos} alunos</div>
            </div>
            <div className="flex items-center gap-2">
              <div
                className="px-2 py-0.5 rounded-full text-[9px] font-bold"
                style={{ background: `${TURNO_COLORS[t.nome]}22`, color: TURNO_COLORS[t.nome] }}
              >
                {t.totalAlunos} alunos
              </div>
              <span className={`text-xs ${isLight ? 'text-amber-600' : 'text-[#f9e2af]/30'}`}>→</span>
            </div>
          </button>
        ))}
        {data.turnos.length === 0 && <Empty msg="Nenhum turno registrado" theme={theme} />}
      </div>
      {turnoDialogNome && (
        <TurnoDialog
          turnoNome={turnoDialogNome}
          data={data}
          onClose={() => setTurnoDialogNome(null)}
          theme={theme}
        />
      )}
    </SectionWrapper>
  );

  if (activeCard === 'professores') {
    // Agrupa turmas por turno para cada professor
    return (
      <SectionWrapper title={`${data.totais.professores} Professores`} color="#89b4fa" theme={theme}>
        <div className="flex flex-col gap-3">
          {(data.professores as ProfessorActor[]).map(prof => {
            const isFem = prof.genero === 'FEMININO';
            const fallback = isFem ? '/img/avatars/f/01.jpg' : '/img/avatars/m/01.jpg';
            // Garante que turmas é sempre um array (backend pode retornar undefined durante transição)
            const profTurmas: ProfessorActor['turmas'] = Array.isArray(prof.turmas) ? prof.turmas : [];
            // Agrupa turmas por turno
            const turnoGroup: Record<string, typeof profTurmas> = {};
            for (const t of profTurmas) {
              if (!turnoGroup[t.turno]) turnoGroup[t.turno] = [];
              turnoGroup[t.turno].push(t);
            }
            const turnos = Object.keys(turnoGroup);

            return (
              <div key={prof.id}
                className={`flex gap-3 rounded-xl border px-3 py-3 transition-all ${isLight ? 'bg-white border-slate-200 shadow-sm hover:bg-slate-50' : 'border-white/8 bg-white/2 hover:bg-white/4'}`}
              >
                {/* Avatar + nome + matéria */}
                <div className="flex flex-col items-center gap-1 flex-shrink-0 w-[58px]">
                  <div className={`w-10 h-10 rounded-full overflow-hidden border-2 ${isLight ? 'border-slate-200' : 'border-[#89b4fa]/20'}`}>
                    <img src={prof.fotoUrl || fallback} alt={prof.nome}
                         className="w-full h-full object-cover"
                         onError={e => { (e.target as HTMLImageElement).src = fallback; }} />
                  </div>
                  <span className={`text-[10px] text-center leading-tight line-clamp-2 font-medium ${isLight ? 'text-slate-700' : 'text-white'}`}>
                    {prof.nome.split(' ')[0]}
                  </span>
                  {prof.materiaPrincipal && (
                    <span className={`text-[8px] text-center leading-tight line-clamp-2 ${isLight ? 'text-indigo-600 font-semibold' : 'text-[#89b4fa]'}`}>
                      {prof.materiaPrincipal}
                    </span>
                  )}
                </div>

                {/* Turmas agrupadas por turno */}
                <div className="flex-1 min-w-0 flex flex-col gap-2">
                  {profTurmas.length === 0 ? (
                    <span className={`text-[10px] italic ${isLight ? 'text-slate-400' : 'text-[#45475a]'}`}>Sem turmas vinculadas</span>
                  ) : (
                    turnos.map(turnoNome => (
                      <div key={turnoNome}>
                        {/* Nome do turno clicável */}
                        <button
                          onClick={() => setTurnoDialogNome(turnoNome)}
                          className="text-[9px] font-bold uppercase tracking-wider mb-1 cursor-pointer hover:opacity-80 transition-opacity bg-transparent border-none"
                          style={{ color: TURNO_COLORS[turnoNome] ?? '#6c7086' }}
                        >
                          {TURNO_LABELS[turnoNome] ?? turnoNome} →
                        </button>
                        {/* Turmas clicáveis */}
                        <div className="flex flex-wrap gap-1">
                          {turnoGroup[turnoNome].map(turma => (
                            <button
                              key={turma.id}
                              onClick={() => setTurmaDialogId(turma.id)}
                              className={`text-[9px] px-1.5 py-0.5 rounded-md border cursor-pointer hover:opacity-80 transition-all ${isLight ? 'text-slate-700' : 'text-[#cdd6f4]'}`}
                              style={{
                                borderColor: (TURNO_COLORS[turnoNome] ?? '#89b4fa') + (isLight ? '80' : '40'),
                                background:  (TURNO_COLORS[turnoNome] ?? '#89b4fa') + (isLight ? '15' : '12'),
                              }}
                            >
                              {turma.nome}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
          {data.professores.length === 0 && <Empty msg="Nenhum professor cadastrado" theme={theme} />}
        </div>

        {/* Dialogs reutilizados */}
        {turmaDialog && <TurmaDialog turma={turmaDialog} onClose={() => setTurmaDialogId(null)} theme={theme} />}
        {turnoDialogNome && <TurnoDialog turnoNome={turnoDialogNome} data={data} onClose={() => setTurnoDialogNome(null)} theme={theme} />}
      </SectionWrapper>
    );
  }

  if (activeCard === 'responsaveis') return (
    <SectionWrapper title={`${data.totais.responsaveis} Responsáveis vinculados`} color="#cba6f7" theme={theme}>
      <AvatarWrap actors={data.responsaveis} theme={theme} />
    </SectionWrapper>
  );

  if (activeCard === 'gestores') return (
    <SectionWrapper title={`${data.totais.gestores} Gestores`} color="#a6e3a1" theme={theme}>
      <div className="flex flex-wrap gap-3">
        {data.gestores.map(g => (
          <div key={g.id} className="flex flex-col items-center gap-1.5 w-[68px]">
            <MiniAvatar actor={g} size={44} theme={theme} />
            <span className={`text-[10px] text-center leading-tight line-clamp-2 font-medium ${isLight ? 'text-slate-700' : 'text-[#cdd6f4]'}`}>{g.nome.split(' ')[0]}</span>
            {g.cargo && (
              <span className={`text-[8px] px-1.5 py-0.5 rounded-full font-bold uppercase ${isLight ? 'bg-emerald-50 text-emerald-600 border border-emerald-200' : 'bg-[#a6e3a1]/15 text-[#a6e3a1]'}`}>{g.cargo}</span>
            )}
          </div>
        ))}
        {data.gestores.length === 0 && <Empty msg="Nenhum gestor cadastrado" theme={theme} />}
      </div>
    </SectionWrapper>
  );

  // ── DISPOSITIVO RFID ──────────────────────────────────────────────────────
  if (activeCard === 'dispositivo') {
    const devs = (data.dispositivos ?? []) as Dispositivo[];
    return (
      <SectionWrapper title={`${devs.length} Dispositivo${devs.length !== 1 ? 's' : ''} RFID`} color="#f5c2e7" theme={theme}>
        <div className="flex flex-col gap-3">
          {devs.length === 0 ? (
            <div className={`rounded-xl border p-4 text-center ${isLight ? 'border-red-200 bg-red-50/50' : 'border-[#f38ba8]/30 bg-[#f38ba8]/5'}`}>
              <div className="text-2xl mb-1">⚠️</div>
              <div className="text-xs font-bold text-red-600">Nenhum leitor RFID instalado</div>
              <div className={`text-[10px] mt-1 ${isLight ? 'text-slate-500' : 'text-[#6c7086]'}`}>O simulador não poderá registrar entradas e saídas sem um dispositivo ativo.</div>
            </div>
          ) : devs.map(dev => {
            const isOnline = dev.status === 'ONLINE';
            return (
              <div key={dev.id} className={`rounded-xl border p-3 ${isLight ? 'bg-white border-slate-200 shadow-sm' : 'border-white/8 bg-white/2'}`}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                       style={{ background: isOnline ? '#a6e3a1' + '22' : '#f38ba8' + '22' }}>
                    <span className="text-xl">📡</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-bold truncate ${isLight ? 'text-slate-800' : 'text-white'}`}>{dev.nome}</span>
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold"
                            style={{ 
                              background: isOnline ? (isLight ? '#d1fae5' : '#a6e3a122') : (isLight ? '#fee2e2' : '#f38ba822'), 
                              color: isOnline ? (isLight ? '#065f46' : '#a6e3a1') : (isLight ? '#991b1b' : '#f38ba8') 
                            }}>
                        {dev.status}
                      </span>
                    </div>
                    <div className={`text-[10px] mt-0.5 ${isLight ? 'text-slate-500 font-medium' : 'text-[#6c7086]'}`}>{dev.tipo} · {dev.codigo}</div>
                    {dev.localInstalacao && <div className={`text-[10px] mt-0.5 ${isLight ? 'text-slate-600 font-semibold' : 'text-[#a6adc8]'}`}>📍 {dev.localInstalacao}</div>}
                    {dev.ipLocal && <div className={`text-[10px] mt-0.5 font-mono ${isLight ? 'text-slate-500' : 'text-[#6c7086]'}`}>{dev.ipLocal}</div>}
                    {dev.identificadorFisico && <div className="text-[9px] text-slate-400 mt-0.5 font-mono">ID: {dev.identificadorFisico}</div>}
                    {dev.ultimoHeartbeatEm && (
                      <div className={`text-[9px] mt-1 ${isLight ? 'text-slate-400 font-mono' : 'text-[#45475a]'}`}>Último ping: {new Date(dev.ultimoHeartbeatEm).toLocaleString('pt-BR')}</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </SectionWrapper>
    );
  }

  // ── CHAVEIROS (TagRfid) ───────────────────────────────────────────────────
  if (activeCard === 'chaveiros') {
    const alunosCast = data.alunos as AlunoActor[];
    const comTag     = alunosCast.filter(a => a.hasTag);
    const semTag     = alunosCast.filter(a => !a.hasTag);
    const pct        = alunosCast.length > 0 ? Math.round((comTag.length / alunosCast.length) * 100) : 0;
    return (
      <SectionWrapper title={`${data.totais.chaveiros ?? comTag.length} Chaveiros ativos`} color="#fab387" theme={theme}>
        {/* Barra de progresso */}
        <div className={`rounded-xl border p-3 flex flex-col gap-2 ${isLight ? 'bg-white border-slate-200 shadow-sm' : 'border-white/8 bg-white/2'}`}>
          <div className="flex justify-between items-center">
            <span className={`text-[10px] ${isLight ? 'text-slate-500 font-medium' : 'text-[#a6adc8]'}`}>Cobertura RFID</span>
            <span className="text-[10px] font-bold" style={{ color: pct >= 80 ? '#059669' : pct >= 50 ? '#d97706' : '#dc2626' }}>{pct}%</span>
          </div>
          <div className={`w-full h-1.5 rounded-full overflow-hidden ${isLight ? 'bg-slate-100' : 'bg-white/8'}`}>
            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: pct >= 80 ? '#059669' : pct >= 50 ? '#d97706' : '#dc2626' }} />
          </div>
          <div className="flex gap-3 mt-1">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-[#059669]" />
              <span className={`text-[9px] ${isLight ? 'text-slate-500 font-medium' : 'text-[#a6adc8]'}`}>{comTag.length} com chaveiro</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-[#dc2626]" />
              <span className={`text-[9px] ${isLight ? 'text-slate-500 font-medium' : 'text-[#a6adc8]'}`}>{semTag.length} sem chaveiro</span>
            </div>
          </div>
        </div>

        {/* Alunos sem chaveiro */}
        {semTag.length > 0 && (
          <div className="mt-3">
            <div className={`text-[9px] font-bold uppercase tracking-wider mb-2 ${isLight ? 'text-red-600' : 'text-[#f38ba8]'}`}>⚠️ Alunos sem chaveiro ({semTag.length})</div>
            <div className="flex flex-wrap gap-2">
              {semTag.slice(0, 40).map(a => {
                const isFem    = a.genero === 'FEMININO';
                const fallback = isFem ? '/img/avatars/f/01.jpg' : '/img/avatars/m/01.jpg';
                return (
                  <div key={a.id} className="relative group flex-shrink-0">
                    <div className={`w-8 h-8 rounded-full overflow-hidden border opacity-75 ${isLight ? 'border-red-200' : 'border-[#f38ba8]/30'}`}>
                      <img src={a.fotoUrl || fallback} alt={a.nome}
                           className="w-full h-full object-cover"
                           onError={e => { (e.target as HTMLImageElement).src = fallback; }} />
                    </div>
                    <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#dc2626] flex items-center justify-center ${isLight ? 'border-white' : 'border-[#1e1e2e]'}`}>
                      <span className="text-white font-bold" style={{ fontSize: 7, lineHeight: 1 }}>!</span>
                    </div>
                  </div>
                );
              })}
              {semTag.length > 40 && <div className={`text-[10px] self-center ${isLight ? 'text-slate-400' : 'text-[#6c7086]'}`}>+{semTag.length - 40} mais</div>}
            </div>
          </div>
        )}
      </SectionWrapper>
    );
  }

  // ── MUNICÍPIO ────────────────────────────────────────────────────────────
  if (activeCard === 'municipio') {
    const m = data.municipio as Municipio | null;
    if (!m) return (
      <SectionWrapper title="Município" color="#94e2d5" theme={theme}>
        <Empty msg="Município não vinculado à escola" theme={theme} />
      </SectionWrapper>
    );
    const primary = m.corPrimaria ?? '#94e2d5';
    return (
      <SectionWrapper title={m.nomeOficial ?? m.nome} color={primary} theme={theme}>
        <div className={`rounded-xl border p-4 flex flex-col gap-3 ${isLight ? 'bg-white border-slate-200 shadow-sm' : 'border-white/8 bg-white/2'}`}>
          {/* Cabeçalho município */}
          <div className="flex items-center gap-3">
            {m.logoUrl ? (
              <img src={m.logoUrl} alt="Brasão" className="w-10 h-10 object-contain" />
            ) : (
              <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl" style={{ background: primary + '22' }}>🏙️</div>
            )}
            <div>
              <div className={`text-xs font-bold ${isLight ? 'text-slate-800' : 'text-white'}`}>{m.nome}</div>
              {m.slogan && <div className={`text-[9px] italic mt-0.5 ${isLight ? 'text-slate-500' : 'text-[#6c7086]'}`}>"{m.slogan}"</div>}
            </div>
          </div>

          {/* Prefeito */}
          {m.nomePrefeito && (
            <div className={`flex items-center gap-3 border-t pt-3 ${isLight ? 'border-slate-100' : 'border-white/5'}`}>
              {m.fotoPrefeito ? (
                <img src={m.fotoPrefeito} alt={m.nomePrefeito} className={`w-10 h-10 rounded-full object-cover border ${isLight ? 'border-slate-200' : 'border-white/10'}`} />
              ) : (
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-xl bg-white/5">👤</div>
              )}
              <div>
                <div className={`text-xs font-bold ${isLight ? 'text-slate-800' : 'text-white'}`}>{m.nomePrefeito}</div>
                <div className={`text-[9px] ${isLight ? 'text-slate-500' : 'text-[#6c7086]'}`}>{m.cargoPrefeito ?? 'Prefeito Municipal'}</div>
              </div>
            </div>
          )}

          {/* Demais campos */}
          <div className={`grid grid-cols-1 gap-1.5 text-[10px] ${isLight ? 'text-slate-600' : ''}`}>
            {m.nomeVicePrefeito && (
              <div className="flex justify-between">
                <span className={isLight ? 'text-slate-400' : 'text-[#6c7086]'}>Vice-Prefeito</span>
                <span className={`font-semibold ${isLight ? 'text-slate-700' : 'text-[#a6adc8]'}`}>{m.nomeVicePrefeito}</span>
              </div>
            )}
            {m.nomeSecretario && (
              <div className="flex justify-between">
                <span className={isLight ? 'text-slate-400' : 'text-[#6c7086]'}>Sec. Educação</span>
                <span className={`font-semibold ${isLight ? 'text-slate-700' : 'text-[#a6adc8]'}`}>{m.nomeSecretario}</span>
              </div>
            )}
            {m.telefone && (
              <div className="flex justify-between">
                <span className={isLight ? 'text-slate-400' : 'text-[#6c7086]'}>Telefone</span>
                <span className={isLight ? 'text-slate-700 font-semibold' : 'text-[#a6adc8]'}>{m.telefone}</span>
              </div>
            )}
            {m.emailInstitucional && (
              <div className="flex justify-between">
                <span className={isLight ? 'text-slate-400' : 'text-[#6c7086]'}>Email</span>
                <span className={`truncate max-w-[140px] ${isLight ? 'text-slate-700 font-semibold' : 'text-[#a6adc8]'}`}>{m.emailInstitucional}</span>
              </div>
            )}
            {m.siteOficial && (
              <div className="flex justify-between">
                <span className={isLight ? 'text-slate-400' : 'text-[#6c7086]'}>Site</span>
                <a href={m.siteOficial} target="_blank" rel="noreferrer"
                   className="text-blue-600 truncate max-w-[140px] hover:underline font-semibold">{m.siteOficial}</a>
              </div>
            )}
          </div>
        </div>
      </SectionWrapper>
    );
  }

  return null;
}

function SectionWrapper({ title, color, children, theme = 'dark' }: { title: string; color: string; children: React.ReactNode; theme?: 'dark' | 'light' }) {
  const isLight = theme === 'light';
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <div className="h-[2px] w-3 rounded-full" style={{ background: color }} />
        <span className={`text-[10px] font-bold uppercase tracking-wider ${isLight ? 'text-slate-500 font-semibold' : ''}`} style={{ color: isLight ? undefined : color }}>{title}</span>
      </div>
      {children}
    </div>
  );
}

function StatRow({ items, theme = 'dark' }: { items: { label: string; value: number }[]; theme?: 'dark' | 'light' }) {
  const isLight = theme === 'light';
  return (
    <div className="flex gap-2 flex-wrap mb-1">
      {items.map(i => (
        <div key={i.label} className={`flex items-center gap-1.5 text-[10px] rounded-lg px-2.5 py-1.5 border ${
          isLight ? 'text-slate-600 bg-white border-slate-200 shadow-sm' : 'text-[#a6adc8] bg-white/4 border-transparent'
        }`}>
          <span className={`font-bold ${isLight ? 'text-slate-800' : 'text-white'}`}>{i.value}</span>
          <span>{i.label}</span>
        </div>
      ))}
    </div>
  );
}

function AvatarWrap({ actors, theme = 'dark' }: { actors: Actor[]; theme?: 'dark' | 'light' }) {
  if (actors.length === 0) return <Empty msg="Nenhum registro encontrado" theme={theme} />;
  return (
    <div className="flex flex-wrap gap-2">
      {actors.map(a => <MiniAvatar key={a.id} actor={a} size={36} theme={theme} />)}
    </div>
  );
}

function Empty({ msg, theme = 'dark' }: { msg: string; theme?: 'dark' | 'light' }) {
  const isLight = theme === 'light';
  return <div className={`text-[10px] py-3 text-center ${isLight ? 'text-slate-400 font-medium' : 'text-[#45475a]'}`}>{msg}</div>;
}

// ── Componente principal ──────────────────────────────────────────────────────
interface SchoolDetailPanelNewProps {
  school: DemoSchool;
  theme: 'dark' | 'light';
  onDataLoaded?: (data: PanelData) => void;
}

export function SchoolDetailPanelNew({ school, theme, onDataLoaded }: SchoolDetailPanelNewProps) {
  const [data, setData]         = useState<PanelData | null>(null);
  const [loading, setLoading]   = useState(false);
  const [activeCard, setActive] = useState<string>('alunos');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await simulationApi.getSchoolPanelDetail(school.id);
      setData(d);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [school.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (data && onDataLoaded) {
      onDataLoaded(data);
    }
  }, [data, onDataLoaded]);

  // totais do card — usa os do DemoSchool enquanto carrega
  const totais = data?.totais ?? {
    alunos:       school.totalAlunos,
    turmas:       school.totalTurmas,
    turnos:       0,
    professores:  school.totalProfessores,
    responsaveis: school.totalResponsaveis,
    gestores:     school.totalGestores,
    dispositivo:  0,
    chaveiros:    0,
    municipio:    1,
  };

  const isLight = theme === 'light';

  return (
    <div className="flex flex-col gap-4 h-full overflow-y-auto">

      {/* KPI cards */}
      <div className="flex gap-1.5 flex-wrap">
        {CARD_CONFIG.map(c => (
          <KpiCard
            key={c.key}
            icon={c.icon}
            label={c.label}
            value={totais[c.key] ?? 0}
            color={c.color}
            isActive={activeCard === c.key}
            onClick={() => setActive(c.key)}
            theme={theme}
          />
        ))}
      </div>

      {/* Conteúdo do card selecionado */}
      {loading ? (
        <div className={`flex items-center justify-center gap-2 py-8 text-xs ${isLight ? 'text-slate-400' : 'text-[#a6adc8]/50'}`}>
          <div className="w-4 h-4 border-2 border-[#cba6f7] border-t-transparent rounded-full animate-spin" />
          Carregando detalhes…
        </div>
      ) : data ? (
        <CardSection activeCard={activeCard} data={data} theme={theme} />
      ) : (
        <div className="text-[10px] text-[#f38ba8]/60 text-center py-6">
          Erro ao carregar dados da escola.
        </div>
      )}
    </div>
  );
}
